import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../hooks/useLanguage';
import { bookingsApi, userApi, tripsApi, discountCodesApi } from '../services/api';
import { getDatabaseId, getDatabaseName } from '../data/tripData';
import { TripDate, Participant } from '../types';
import { formatDate } from '../utils/dateFormatter';
import toast from 'react-hot-toast';

interface UserAddress {
  address: string;
  city: string;
  postalCode: string;
  country: string;
  phone: string;
}

interface BookingFormProps {
  tripId: string;
  tripName: string;
  totalPrice: number;
  tripWithDates?: any;
  onBookingSuccess: (bookingId: string) => void;
  onCancel: () => void;
}

export const BookingForm: React.FC<BookingFormProps> = ({
  tripId,
  tripName,
  totalPrice,
  tripWithDates: propTripWithDates,
  onBookingSuccess,
  onCancel
}) => {
  const { t, language } = useLanguage();
  const { user, refreshUser } = useAuth();
  const [numberOfQuotes, setNumberOfQuotes] = useState(1);
  const [participants, setParticipants] = useState<Participant[]>([
    { firstName: '', lastName: '', age: 0 }
  ]);
  const [userAddress, setUserAddress] = useState<UserAddress>({
    address: '',
    city: '',
    postalCode: '',
    country: 'Italia',
    phone: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [step, setStep] = useState<'date' | 'quotes' | 'participants' | 'address' | 'review'>('date');
  const [tripData, setTripData] = useState<any>(null);
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const [loadingTripData, setLoadingTripData] = useState(false);
  const [tripDates, setTripDates] = useState<any[]>([]);
  const [selectedTripDate, setSelectedTripDate] = useState<any>(null);
  const [selectedParticipationType, setSelectedParticipationType] = useState<string>('');
  const [selectedTripPricing, setSelectedTripPricing] = useState<any>(null);
  const [codiceSconto, setCodiceSconto] = useState('');
  const [codiceScontoValidato, setCodiceScontoValidato] = useState<any>(null);
  const [validatingCode, setValidatingCode] = useState(false);

  // Load trip data with available dates
  useEffect(() => {
    if (propTripWithDates) {
      // Use data passed from parent component
      setTripData(propTripWithDates);
      setLoadingTripData(false);
    } else {
      // Fallback: load data if not provided
      const loadTripData = async () => {
        try {
          setLoadingTripData(true);
          const databaseName = getDatabaseName(tripName);
          const response = await tripsApi.getTripsByName(databaseName);
          if (response.success) {
            setTripData(response.trip);
          } else {
            toast.error(t('booking.error.loadingDates'));
          }
        } catch (error: any) {
          console.error('Error loading trip data:', error);
          if (error.response?.status === 404) {
            toast.error(t('booking.error.noDatesAvailable'));
          } else {
            toast.error(t('booking.error.loadingDates'));
          }
        } finally {
          setLoadingTripData(false);
        }
      };

      loadTripData();
    }
  }, [tripName, propTripWithDates]);

  // Load room configuration and pricing
  useEffect(() => {
    const loadTripData = async () => {
      try {
        // Trip data is already available, no need to load additional dates
      } catch (error) {
        console.error('Error loading trip data:', error);
        toast.error(t('booking.error.loadingTripConfig'));
      }
    };

    loadTripData();
  }, []);

  // Set selectedTripDate when selectedDate changes
  useEffect(() => {
    if (selectedDate) {
      setSelectedTripDate(selectedDate);
    }
  }, [selectedDate]);

  // Load user address if available
  useEffect(() => {
    if (user) {
      setUserAddress({
        address: user.address || '',
        city: user.city || '',
        postalCode: user.postalCode || '',
        country: user.country || 'Italia',
        phone: user.phone || ''
      });
    }
  }, [user]);

  // Update participants when numberOfQuotes changes
  useEffect(() => {
    const newParticipants = [];
    for (let i = 0; i < numberOfQuotes; i++) {
      newParticipants.push(participants[i] || { firstName: '', lastName: '' });
    }
    setParticipants(newParticipants);
  }, [numberOfQuotes]);

  // Reset participation type selection if current selection is not available for the selected date
  useEffect(() => {
    if (selectedDate && selectedParticipationType) {
      const availableSpots = selectedDate?.availableSpots || 0;
      const requiredSpots = selectedParticipationType === 'solo' ? 1 : 
                           selectedParticipationType === 'duo' ? 2 : 
                           selectedParticipationType === 'trio' ? 3 : 4;
      
      if (availableSpots < requiredSpots) {
        setSelectedParticipationType('');
        setSelectedTripPricing(null);
        setNumberOfQuotes(0);
      }
    }
  }, [selectedDate, selectedParticipationType]);

  const handleParticipantChange = (index: number, field: keyof Participant, value: string | number) => {
    const newParticipants = [...participants];
    newParticipants[index] = { ...newParticipants[index], [field]: value };
    setParticipants(newParticipants);
  };

  const handleAddressChange = (field: keyof UserAddress, value: string) => {
    setUserAddress(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = () => {
    switch (step) {
      case 'date':
        return selectedDate !== null;
      case 'quotes':
        return selectedParticipationType !== '' && selectedTripPricing !== null;
      case 'participants':
        // Basic validation: all participants must have name and age
        const basicValidation = participants.every(p => p.firstName.trim() && p.lastName.trim() && p.age > 0);
        
        if (!basicValidation) return false;
        
        // Check for at least one adult (18+ years old) in all trip types
        const adults18Plus = participants.filter(p => p.age >= 18);
        if (adults18Plus.length === 0) {
          return false;
        }
        
        // Age validation for trips with 3 or 4 people (family trips)
        if (selectedParticipationType === 'trio' || selectedParticipationType === 'pocker') {
          // Count adults (age >= 16) and children (age < 16)
          const adults16Plus = participants.filter(p => p.age >= 16);
          const children = participants.filter(p => p.age < 16);
          
          // For family trips, we need at least one child and maximum 2 adults (16+)
          if (children.length === 0) {
            return false; // No children found
          }
          
          if (adults16Plus.length > 2) {
            return false; // Too many adults
          }
        }
        
        return true;
      case 'address':
        return userAddress.address.trim() && userAddress.city.trim() && 
               userAddress.postalCode.trim() && userAddress.country.trim();
      default:
        return true;
    }
  };

  const nextStep = () => {
    // Don't proceed if button is disabled
    if (isNextButtonDisabled()) {
      return;
    }
    
    if (validateStep()) {
      switch (step) {
        case 'date':
          setStep('quotes');
          break;
        case 'quotes':
          setStep('participants');
          break;
        case 'participants':
          setStep('address');
          break;
        case 'address':
          setStep('review');
          break;
      }
    } else {
      if (step === 'quotes' && selectedDate?.availableSpots !== null && numberOfQuotes > (selectedDate?.availableSpots || 0)) {
        toast.error(`${t('booking.spotsRemaining')}: ${selectedDate?.availableSpots}`);
      } else if (step === 'participants') {
        // Check for specific age validation errors
        const adults18Plus = participants.filter(p => p.age >= 18);
        
        // First check if there's at least one adult (18+)
        if (adults18Plus.length === 0) {
          toast.error(t('booking.validation.atLeastOneAdult18'));
          return;
        }
        
        // Then check family trip specific rules
        if (selectedParticipationType === 'trio' || selectedParticipationType === 'pocker') {
          const adults16Plus = participants.filter(p => p.age >= 16);
          const children = participants.filter(p => p.age < 16);
          
          if (children.length === 0) {
            toast.error(t('booking.validation.familyTripNeedsChild'));
          } else if (adults16Plus.length > 2) {
            toast.error(t('booking.validation.familyTripMaxAdults').replace('{count}', adults16Plus.length.toString()));
          } else {
            toast.error(t('booking.validation.fillAllParticipantFields'));
          }
        } else {
          toast.error(t('booking.validation.fillAllParticipantFields'));
        }
      } else {
        toast.error(t('booking.validationError'));
      }
    }
  };

  const prevStep = () => {
    switch (step) {
      case 'quotes':
        setStep('date');
        break;
      case 'participants':
        setStep('quotes');
        break;
      case 'address':
        setStep('participants');
        break;
      case 'review':
        setStep('address');
        break;
    }
  };

  const saveAddressToDatabase = async () => {
    try {
      await userApi.updateProfile(userAddress);
      console.log('Address saved to database');
      // Aggiorna il contesto dell'utente con i nuovi dati
      await refreshUser();
    } catch (error) {
      console.error('Error saving address:', error);
      // Non blocchiamo il processo di prenotazione se il salvataggio dell'indirizzo fallisce
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) {
      toast.error(t('booking.validationError'));
      return;
    }

    setIsProcessing(true);
    try {
      // Prima salva l'indirizzo nel database
      await saveAddressToDatabase();
      
      if (!selectedDate) {
        toast.error(t('booking.error.selectDateForTrip'));
        return;
      }

      // Get discount code ID from localStorage if available
      let codiceScontoId: string | undefined;
      if (selectedDate && user?.id && codiceScontoValidato) {
        const savedCodeId = localStorage.getItem(`codiceSconto_${selectedDate.id}_${user.id}`);
        if (savedCodeId) {
          codiceScontoId = savedCodeId;
        }
      }

      const response = await bookingsApi.createBooking({
        tripId: selectedDate.id,
        numberOfQuotes,
        participants,
        userAddress,
        participationType: selectedParticipationType,
        codiceScontoId
      });

      if (response.success && response.checkoutUrl) {
        onBookingSuccess(response.booking.id);
        window.location.href = response.checkoutUrl;
      } else {
        throw new Error('Failed to create booking');
      }
    } catch (error: any) {
      console.error('Error creating booking:', error);
      
      // Check for specific error messages
      if (error.response?.data?.message) {
        const errorMessage = error.response.data.message;
        
        if (errorMessage.includes('already have a booking for this trip')) {
          toast.error(t('booking.alreadyBookedError'));
        } else {
          toast.error(errorMessage);
        }
      } else {
        toast.error(t('booking.createError'));
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleNumberOfQuotesChange = (newNumber: number) => {
    setNumberOfQuotes(newNumber);
    
    // Update participants array
    const newParticipants = [];
    for (let i = 0; i < newNumber; i++) {
      newParticipants.push(participants[i] || { firstName: '', lastName: '', age: 0 });
    }
    setParticipants(newParticipants);
  };

  const handleParticipationTypeChange = (participationType: string) => {
    setSelectedParticipationType(participationType);
    
    if (!selectedDate) return;
    
    const pricing = {
      id: `${selectedDate.id}-${participationType}`,
      participationType,
      occupancy: participationType === 'solo' ? 1 : participationType === 'duo' ? 2 : participationType === 'trio' ? 3 : 4,
      totalPrice: participationType === 'solo' ? (selectedDate as any).priceSingle :
                  participationType === 'duo' ? (selectedDate as any).priceDuo :
                  participationType === 'trio' ? (selectedDate as any).priceTrio :
                  (selectedDate as any).pricePocker,
      depositPerPerson: (selectedDate as any).deposit,
      isActive: true
    };
    
    setSelectedTripPricing(pricing);
    
    // Update number of quotes based on participation type occupancy
    if (pricing) {
      setNumberOfQuotes(pricing.occupancy);
      const newParticipants = [];
      for (let i = 0; i < pricing.occupancy; i++) {
        newParticipants.push(participants[i] || { firstName: '', lastName: '', age: 0 });
      }
      setParticipants(newParticipants);
    }
  };

  // Calculate total amount: price per person * number of people
  // IMPORTANT: selectedTripPricing.totalPrice is per person, must multiply by occupancy
  const totalAmount = selectedTripPricing 
    ? selectedTripPricing.totalPrice * selectedTripPricing.occupancy 
    : (tripData?.totalPrice || totalPrice) * numberOfQuotes;
  const depositAmount = selectedTripPricing ? selectedTripPricing.depositPerPerson * selectedTripPricing.occupancy : Math.round(totalAmount * 0.3);
  const balanceAmount = totalAmount - depositAmount;

  // Calculate discount if code is validated
  // IMPORTANT: Discount applies ONLY to total, NOT to deposit
  const importoOriginale = totalAmount;
  const percentualeSconto = codiceScontoValidato?.percentualeSconto || 0;
  const importoScontato = percentualeSconto > 0 ? importoOriginale * (1 - percentualeSconto / 100) : importoOriginale;
  const scontoApplicato = importoOriginale - importoScontato;

  // Deposit amount stays the same (NOT discounted)
  // Balance = Total discounted - Deposit (original)
  const depositAmountScontato = depositAmount; // Deposit is NOT discounted
  const balanceAmountScontato = percentualeSconto > 0 ? importoScontato - depositAmount : balanceAmount;

  // Validate discount code
  const handleValidateCode = async () => {
    if (!codiceSconto.trim()) {
      toast.error(t('booking.discountCode.enterCodeError'));
      return;
    }

    if (!selectedDate) {
      toast.error(t('booking.discountCode.selectDateFirst'));
      return;
    }

    setValidatingCode(true);
    try {
      const response = await discountCodesApi.validateCode(codiceSconto.toUpperCase(), selectedDate.id);
      
      if (response.valid) {
        setCodiceScontoValidato(response.codiceSconto);
        // Save to localStorage for later use in payment
        localStorage.setItem(`codiceSconto_${selectedDate.id}_${user?.id}`, response.codiceSconto.id);
        toast.success(t('booking.discountCode.appliedMessage').replace('{percent}', response.codiceSconto.percentualeSconto.toString()));
      } else {
        toast.error(response.message || t('booking.discountCode.invalid'));
        setCodiceScontoValidato(null);
      }
    } catch (error: any) {
      console.error('Error validating discount code:', error);
      const errorMessage = error.response?.data?.message || t('booking.discountCode.validationError');
      toast.error(errorMessage);
      setCodiceScontoValidato(null);
    } finally {
      setValidatingCode(false);
    }
  };

  const handleRemoveCode = () => {
    setCodiceSconto('');
    setCodiceScontoValidato(null);
    if (selectedDate && user?.id) {
      localStorage.removeItem(`codiceSconto_${selectedDate.id}_${user.id}`);
    }
    toast.success(t('booking.discountCode.removed'));
  };


  const renderDateStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {t('booking.selectTripDate')}
        </h3>
        <p className="text-gray-600">
          {t('booking.selectDateDescription')} {tripName}
        </p>
      </div>

      {loadingTripData ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : tripData && tripData.availableDates.length > 0 ? (
        <div className="space-y-4">
          {tripData.availableDates.map((date: any) => {
            const isAvailable = date.availableSpots !== null && date.availableSpots > 0;
            const isFullyBooked = date.availableSpots === 0;
            
            return (
              <div
                key={date.id}
                onClick={() => isAvailable ? setSelectedDate(date) : null}
                className={`p-4 border-2 rounded-lg transition-colors ${
                  !isAvailable 
                    ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60'
                    : selectedDate?.id === date.id
                    ? 'border-blue-500 bg-blue-50 cursor-pointer'
                    : 'border-gray-200 hover:border-gray-300 cursor-pointer'
                }`}
              >
                <div className="flex justify-between items-start">
                  <div>
                    {date.endDate && date.endDate !== date.startDate ? (
                      <h4 className="font-medium text-gray-900">
                        {formatDate(date.startDate, language)} - {formatDate(date.endDate, language)}
                      </h4>
                    ) : (
                      <h4 className="font-medium text-gray-900">
                        {formatDate(date.startDate, language)}
                      </h4>
                    )}
                  </div>
                  <div className="text-right">
                    {date.availableSpots !== null ? (
                      <div>
                        {isFullyBooked ? (
                          <p className="text-sm text-red-600 font-medium">
                            {t('booking.fullyBooked')}
                          </p>
                        ) : (
                          <p className="text-sm text-gray-600">
                            {date.availableSpots} {t('booking.spotsAvailable')}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-600">
                        {date.maxParticipants} {t('booking.spotsTotalLabel')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-600">{t('booking.noDatesAvailable')}</p>
        </div>
      )}
    </div>
  );

  const renderRoomStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {t('booking.selectPackageTitle')}
        </h3>
        <p className="text-gray-600">
          {t('booking.selectPackageSubtitle')}
        </p>
      </div>


      <div className="grid gap-4">
        {selectedDate && (selectedDate as any).priceSingle ? (
          [
            {
              id: `${selectedDate.id}-solo`,
              participationType: 'solo',
              occupancy: 1,
              totalPrice: (selectedDate as any).priceSingle,
              depositPerPerson: (selectedDate as any).deposit,
              isActive: true
            },
            {
              id: `${selectedDate.id}-duo`,
              participationType: 'duo',
              occupancy: 2,
              totalPrice: (selectedDate as any).priceDuo,
              depositPerPerson: (selectedDate as any).deposit,
              isActive: true
            },
            {
              id: `${selectedDate.id}-trio`,
              participationType: 'trio',
              occupancy: 3,
              totalPrice: (selectedDate as any).priceTrio,
              depositPerPerson: (selectedDate as any).deposit,
              isActive: true
            },
            {
              id: `${selectedDate.id}-pocker`,
              participationType: 'pocker',
              occupancy: 4,
              totalPrice: (selectedDate as any).pricePocker,
              depositPerPerson: (selectedDate as any).deposit,
              isActive: true
            }
          ].map((pricing) => {
            const availableSpots = selectedDate?.availableSpots || 0;
            const isDisabled = availableSpots < pricing.occupancy;
            
            return (
            <div
              key={pricing.id}
              className={`border-2 rounded-lg p-4 transition-colors ${
                isDisabled 
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                  : selectedParticipationType === pricing.participationType
                    ? 'border-blue-500 bg-blue-50 cursor-pointer'
                    : 'border-gray-200 hover:border-gray-300 cursor-pointer'
              }`}
              onClick={() => !isDisabled && handleParticipationTypeChange(pricing.participationType)}
            >
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="font-semibold text-gray-900 capitalize">
                    {pricing.participationType === 'solo' && t('booking.tripTypes.solo')}
                    {pricing.participationType === 'duo' && t('booking.tripTypes.duo')}
                    {pricing.participationType === 'trio' && t('booking.tripTypes.trio')}
                    {pricing.participationType === 'pocker' && t('booking.tripTypes.pocker')}
                  </h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {pricing.occupancy === 1 && t('booking.tripDescriptions.solo')}
                    {pricing.occupancy === 2 && t('booking.tripDescriptions.duo')}
                    {pricing.occupancy === 3 && t('booking.tripDescriptions.trio')}
                    {pricing.occupancy === 4 && t('booking.tripDescriptions.pocker')}
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600">
                    €{pricing.totalPrice.toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500">{t('booking.pricePerPerson')}</div>
                  <div className="text-sm text-gray-500">
                    {t('booking.depositPerPersonLabel')}: €{pricing.depositPerPerson.toFixed(2)}
                  </div>
                </div>
              </div>
              {isDisabled && (
                <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">
                    {t('booking.insufficientSpotsForPackage').replace('{required}', pricing.occupancy.toString()).replace('{available}', availableSpots.toString())}
                  </p>
                </div>
              )}
            </div>
            );
          })
        ) : null}
      </div>

      {(!selectedDate || !(selectedDate as any).priceSingle) && (
        <div className="text-center py-8">
          <p className="text-gray-600">{t('booking.error.noPriceAvailable')}</p>
        </div>
      )}
      <div className="pt-2 text-xs text-gray-500 italic">
        {t('booking.childrenDefinition')}
      </div>
    </div>
  );

  const renderQuotesStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {t('booking.selectQuotes')}
        </h3>
        <p className="text-gray-600">
          {t('booking.selectQuotesDescription')}
        </p>
      </div>

      <div className="bg-blue-50 p-6 rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <span className="text-lg font-medium text-gray-900">
            {t('booking.numberOfQuotes')}
          </span>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setNumberOfQuotes(Math.max(1, numberOfQuotes - 1))}
              className="w-8 h-8 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50"
            >
              -
            </button>
            <span className="text-xl font-bold text-gray-900 w-12 text-center">
              {numberOfQuotes}
            </span>
            <button
              onClick={() => setNumberOfQuotes(Math.min(30, numberOfQuotes + 1))}
              className="w-8 h-8 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50"
            >
              +
            </button>
          </div>
        </div>

        <div className="space-y-2 text-sm text-gray-700">
          <div className="flex justify-between">
            <span>{t('booking.pricePerQuote')}:</span>
            <span>€{tripData?.totalPrice || totalPrice}</span>
          </div>
          {percentualeSconto > 0 && (
            <>
              <div className="flex justify-between text-gray-600">
                <span>{t('booking.totalAmount')}:</span>
                <span className="line-through">€{totalAmount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-green-600 font-semibold">
                <span>{t('booking.totalAmount')} ({t('booking.discount')} {percentualeSconto}%):</span>
                <span>€{importoScontato.toFixed(2)}</span>
              </div>
            </>
          )}
          {!percentualeSconto && (
            <div className="flex justify-between">
              <span>{t('booking.totalAmount')}:</span>
              <span className="font-semibold">€{totalAmount}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span>{t('booking.depositAmount')}:</span>
            <span className={`font-semibold text-blue-600 ${percentualeSconto > 0 ? 'text-green-600' : ''}`}>
              €{percentualeSconto > 0 ? depositAmountScontato.toFixed(2) : depositAmount.toFixed(2)}
            </span>
          </div>
          {selectedDate && (
            <div className="mt-3 pt-2 border-t border-gray-200">
              <p className="text-xs text-gray-600">
                {t('booking.selectedDate')}: {formatDate(selectedDate.startDate, language)}
                {selectedDate.endDate && selectedDate.endDate !== selectedDate.startDate && 
                  ` - ${formatDate(selectedDate.endDate, language)}`
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderParticipantsStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {t('booking.participantsInfo')}
        </h3>
        <p className="text-gray-600">
          {t('booking.participantsDescription')}
        </p>
      </div>

      {/* Family trip rules */}
      {(selectedParticipationType === 'trio' || selectedParticipationType === 'pocker') && (
        <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
          <h4 className="font-semibold text-yellow-800 mb-2">{t('booking.familyRules.title')}</h4>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• {t('booking.familyRules.maxTwoAdults')}</li>
            <li>• {t('booking.familyRules.moreThanTwoAdultsAdvice')}</li>
            <li>• {t('booking.familyRules.atLeastOneAdult')}</li>
          </ul>
        </div>
      )}

      <div className="space-y-4">
        {participants.map((participant, index) => (
          <div key={index} className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-3">
              {t('booking.participant')} {index + 1}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('booking.firstName')} *
                </label>
                <input
                  type="text"
                  value={participant.firstName}
                  onChange={(e) => handleParticipantChange(index, 'firstName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('booking.lastName')} *
                </label>
                <input
                  type="text"
                  value={participant.lastName}
                  onChange={(e) => handleParticipantChange(index, 'lastName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('booking.age')} *
                </label>
                <input
                  type="number"
                  min="0"
                  max="120"
                  value={participant.age}
                  onChange={(e) => handleParticipantChange(index, 'age', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
                {participant.age > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    {participant.age < 16 ? t('booking.child') : t('booking.adult')}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAddressStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {t('booking.contactInfo')}
        </h3>
        <p className="text-gray-600">
          {t('booking.contactDescription')}
        </p>
        {user?.address ? (
          <p className="text-sm text-blue-600 mt-2">
            {t('booking.addressSavedInfo')}
          </p>
        ) : (
          <p className="text-sm text-green-600 mt-2">
            {t('booking.addressWillBeSaved')}
          </p>
        )}
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('booking.address')} *
          </label>
          <input
            type="text"
            value={userAddress.address}
            onChange={(e) => handleAddressChange('address', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('booking.city')} *
            </label>
            <input
              type="text"
              value={userAddress.city}
              onChange={(e) => handleAddressChange('city', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('booking.postalCode')} *
            </label>
            <input
              type="text"
              value={userAddress.postalCode}
              onChange={(e) => handleAddressChange('postalCode', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('booking.country')} *
            </label>
            <select
              value={userAddress.country}
              onChange={(e) => handleAddressChange('country', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="Italia">Italia</option>
              <option value="Francia">Francia</option>
              <option value="Germania">Germania</option>
              <option value="Spagna">Spagna</option>
              <option value="Svizzera">Svizzera</option>
              <option value="Austria">Austria</option>
              <option value="Altro">{t('booking.countryOther')}</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('booking.phone')}
            </label>
            <input
              type="tel"
              value={userAddress.phone}
              onChange={(e) => handleAddressChange('phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderReviewStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">
          {t('booking.reviewBooking')}
        </h3>
        <p className="text-gray-600">
          {t('booking.reviewDescription')}
        </p>
      </div>

      <div className="bg-gray-50 p-6 rounded-lg space-y-4">
        <div>
          <h4 className="font-medium text-gray-900 mb-2">{t('booking.tripDetails')}</h4>
          <p className="text-gray-700">{tripName}</p>
          {selectedDate && (
            <p className="text-sm text-gray-600 mt-1">
              {t('booking.dateLabel')}: {formatDate(selectedDate.startDate, language)}
              {selectedDate.endDate && selectedDate.endDate !== selectedDate.startDate && 
                ` - ${formatDate(selectedDate.endDate, language)}`
              }
            </p>
          )}
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-2">{t('booking.participants')}</h4>
          <div className="space-y-3">
            {participants.map((participant, index) => (
              <div key={index} className="bg-white p-3 rounded border">
                <p className="text-sm font-medium text-gray-500 mb-1">{t('booking.participantNumber')} {index + 1}</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">{t('booking.nameLabel')}:</span>
                    <span className="ml-2 text-gray-900 font-medium">{participant.firstName}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('booking.lastNameLabel')}:</span>
                    <span className="ml-2 text-gray-900 font-medium">{participant.lastName}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">{t('booking.ageLabel')}:</span>
                    <span className="ml-2 text-gray-900 font-medium">{participant.age} {t('booking.yearsOld')}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-2">{t('booking.contactInfo')}</h4>
          <div className="bg-white p-3 rounded border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500">{t('booking.addressLabel')}:</span>
                <span className="ml-2 text-gray-900 font-medium">{userAddress.address}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('booking.cityLabel')}:</span>
                <span className="ml-2 text-gray-900 font-medium">{userAddress.city}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('booking.postalCodeLabel')}:</span>
                <span className="ml-2 text-gray-900 font-medium">{userAddress.postalCode}</span>
              </div>
              <div>
                <span className="text-gray-500">{t('booking.countryLabel')}:</span>
                <span className="ml-2 text-gray-900 font-medium">{userAddress.country}</span>
              </div>
              {userAddress.phone && (
                <div className="md:col-span-2">
                  <span className="text-gray-500">{t('booking.phoneLabel')}:</span>
                  <span className="ml-2 text-gray-900 font-medium">{userAddress.phone}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Discount Code Section */}
        <div className="border-t pt-4">
          <h4 className="font-medium text-gray-900 mb-2">{t('booking.discountCode.title')}</h4>
          {!codiceScontoValidato ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={codiceSconto}
                onChange={(e) => setCodiceSconto(e.target.value.toUpperCase())}
                placeholder={t('booking.discountCode.placeholder')}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={validatingCode}
              />
              <button
                onClick={handleValidateCode}
                disabled={validatingCode || !codiceSconto.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {validatingCode ? t('booking.discountCode.validating') : t('booking.discountCode.apply')}
              </button>
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-md p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-800">
                    {t('booking.discountCode.applied')}: <span className="font-bold">{codiceScontoValidato.codice}</span>
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    {t('booking.discount')} {codiceScontoValidato.percentualeSconto}%
                  </p>
                </div>
                <button
                  onClick={handleRemoveCode}
                  className="text-red-600 hover:text-red-800 text-sm font-medium"
                >
                  {t('booking.remove')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="border-t pt-4">
          <h4 className="font-medium text-gray-900 mb-2">{t('booking.paymentSummary')}</h4>
          <div className="space-y-2 text-sm">
            {percentualeSconto > 0 && (
              <>
                <div className="flex justify-between text-gray-600">
                  <span>{t('booking.originalPrice')}:</span>
                  <span className="line-through">€{importoOriginale.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-green-600 font-semibold">
                  <span>{t('booking.discount')} ({percentualeSconto}%):</span>
                  <span>-€{scontoApplicato.toFixed(2)}</span>
                </div>
                <div className="border-t pt-2"></div>
              </>
            )}
            <div className="flex justify-between">
              <div className="flex flex-col">
                <span>{t('booking.totalAmount')}:</span>
                {selectedTripPricing && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    {percentualeSconto > 0 ? (
                      <>€{selectedTripPricing.totalPrice.toFixed(2)} × {selectedTripPricing.occupancy} = €{totalAmount.toFixed(2)} - €{scontoApplicato.toFixed(2)} ({percentualeSconto}%) = €{importoScontato.toFixed(2)}</>
                    ) : (
                      <>€{selectedTripPricing.totalPrice.toFixed(2)} × {selectedTripPricing.occupancy} = €{totalAmount.toFixed(2)}</>
                    )}
                  </span>
                )}
              </div>
              <span className={`font-semibold ${percentualeSconto > 0 ? 'text-green-600' : ''}`}>
                €{percentualeSconto > 0 ? importoScontato.toFixed(2) : totalAmount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <div className="flex flex-col">
                <span>{t('booking.depositAmount')}:</span>
                {selectedTripPricing && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    €{selectedTripPricing.depositPerPerson.toFixed(2)} × {selectedTripPricing.occupancy} = €{depositAmount.toFixed(2)}
                  </span>
                )}
              </div>
              <span className={`font-semibold text-blue-600 ${percentualeSconto > 0 ? 'text-green-600' : ''}`}>
                €{percentualeSconto > 0 ? depositAmountScontato.toFixed(2) : depositAmount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <div className="flex flex-col">
                <span>{t('booking.balanceAmount')}:</span>
                {selectedTripPricing && (
                  <span className="text-xs text-gray-500 mt-0.5">
                    {percentualeSconto > 0 ? (
                      <>€{importoScontato.toFixed(2)} - €{depositAmount.toFixed(2)} = €{balanceAmountScontato.toFixed(2)}</>
                    ) : (
                      <>€{totalAmount.toFixed(2)} - €{depositAmount.toFixed(2)} = €{balanceAmount.toFixed(2)}</>
                    )}
                  </span>
                )}
              </div>
              <span className={`font-semibold ${percentualeSconto > 0 ? 'text-green-600' : ''}`}>
                €{percentualeSconto > 0 ? balanceAmountScontato.toFixed(2) : balanceAmount.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStepContent = () => {
    switch (step) {
      case 'date':
        return renderDateStep();
      case 'quotes':
        return renderRoomStep();
      case 'participants':
        return renderParticipantsStep();
      case 'address':
        return renderAddressStep();
      case 'review':
        return renderReviewStep();
      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'date':
        return t('booking.selectDate');
      case 'quotes':
        return t('booking.stepPackage');
      case 'participants':
        return t('booking.stepParticipants');
      case 'address':
        return t('booking.stepAddress');
      case 'review':
        return t('booking.stepReview');
      default:
        return '';
    }
  };

  const isNextButtonDisabled = () => {
    return !validateStep();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl xl:max-w-7xl 2xl:max-w-[90vw] w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {getStepTitle()}
            </h2>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              {['date', 'quotes', 'participants', 'address', 'review'].map((stepName, index) => (
                <div key={stepName} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step === stepName ? 'bg-blue-600 text-white' :
                    ['date', 'quotes', 'participants', 'address', 'review'].indexOf(step) > index ? 'bg-green-500 text-white' :
                    'bg-gray-200 text-gray-600'
                  }`}>
                    {index + 1}
                  </div>
                  {index < 4 && (
                    <div className={`w-16 h-1 mx-2 ${
                      ['date', 'quotes', 'participants', 'address', 'review'].indexOf(step) > index ? 'bg-green-500' : 'bg-gray-200'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="mb-6">
            {renderStepContent()}
          </div>

          {/* Navigation */}
          <div className="flex justify-between">
            <button
              onClick={prevStep}
              disabled={step === 'date'}
              className={`px-4 py-2 rounded-md ${
                step === 'date'
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {t('booking.previous')}
            </button>

            <div className="flex space-x-3">
              <button
                onClick={onCancel}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                {t('booking.cancel')}
              </button>

              {step === 'review' ? (
                <button
                  onClick={handleSubmit}
                  disabled={isProcessing}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {isProcessing ? t('booking.processing') : t('booking.proceedToPayment')}
                </button>
              ) : (
                <button
                  onClick={nextStep}
                  disabled={isNextButtonDisabled()}
                  className={`px-4 py-2 rounded-md ${
                    isNextButtonDisabled()
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  {t('booking.next')}
                </button>
              )}
            </div>
          </div>
          
          {/* Help text when Next button is disabled */}
          {isNextButtonDisabled() && step !== 'review' && (
            <div className="mt-3 text-center">
              <p className="text-sm text-gray-500">
                {step === 'date' && t('booking.validation.selectDateToContinue')}
                {step === 'quotes' && t('booking.validation.selectParticipationType')}
                {step === 'participants' && t('booking.validation.fillAllParticipantFields')}
                {step === 'address' && t('booking.validation.fillAllAddressFields')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 